/**
 * Volunteer messages (docs/VOLUNTEER_SIGNUP.md ruling 39;
 * `20261021120000_volunteer_messages.sql`): an organiser emails everyone on a
 * project, a role lead emails their role (or several roles they lead role-wide),
 * and the volunteer app shows the
 * newest messages as a "From <lead>" card.
 *
 * The write contract (`POST …/messages`), the read wire (preview + history),
 * the public unsubscribe body and the coded errors. The SQL is the authority
 * for every rule (sender, recipients, limits, cap); the constants here mirror
 * it for the UI and the API.
 */
import { z } from "zod";
import { surveyText } from "./surveys.js";
import { VOLUNTEER_ERROR_CODES, VOLUNTEER_LOCALES } from "./volunteerSignupReads.js";
// ---------------------------------------------------------------------------
// Constants (mirrors of the SQL)
// ---------------------------------------------------------------------------
/**
 * `role` (one or more roles; admins, or a lead of every chosen role — role-wide
 * when several) or `everyone` (org admins only). No per-shift email.
 */
export const VOLUNTEER_MESSAGE_SCOPES = ["role", "everyone"];
/**
 * `all` of the chosen roles, or `led`: the role's shifts at the events an EVENT
 * lead leads (only they choose; one role only).
 */
export const VOLUNTEER_MESSAGE_AUDIENCES = ["all", "led"];
/** What the caller may send (`volunteer_message_sender_scope`). */
export const VOLUNTEER_MESSAGE_SENDER_SCOPES = ["admin", "role_lead", "event_lead", "none"];
export const VOLUNTEER_MESSAGE_SUBJECT_MAX = 200;
export const VOLUNTEER_MESSAGE_BODY_MAX = 10_000;
/** At most this many recipients per send; above it the send is refused (RV024), never cut short. */
export const VOLUNTEER_MESSAGE_RECIPIENT_CAP = 500;
/** Sends per rolling window: 5 per role (a multi-role send counts against each chosen role), 3 to everyone (RV022). */
export const VOLUNTEER_MESSAGE_LIMITS = { role: 5, everyone: 3 };
export const VOLUNTEER_MESSAGE_WINDOW_HOURS = 24;
/** Recipients hold a shift still to come or that ended at most this many days ago. */
export const VOLUNTEER_MESSAGE_RECENT_DAYS = 7;
/** A queued / sent row older than this without an SES event counts as "not confirmed". */
export const VOLUNTEER_MESSAGE_PENDING_MINUTES = 15;
/** The history page size (`GET …/messages`). */
export const VOLUNTEER_MESSAGE_HISTORY_LIMIT = 20;
/** The newest messages a volunteer's `me` carries. */
export const VOLUNTEER_PUBLIC_MESSAGES_MAX = 5;
/** The raw one-click unsubscribe token (64 hex, generated in SQL; only its sha256 is stored). */
export const VOLUNTEER_UNSUBSCRIBE_TOKEN_RE = /^[0-9a-f]{64}$/;
export function isVolunteerUnsubscribeToken(value) {
    return typeof value === "string" && VOLUNTEER_UNSUBSCRIBE_TOKEN_RE.test(value);
}
/** The SES message tags the dispatcher sets and the events function reads. */
export const VOLUNTEER_MESSAGE_SES_TAGS = { message: "vm_message", recipient: "vm_recipient" };
// ---------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------
const uuid = z.string().uuid();
/** At most this many roles in one send (the multi-role picker). */
export const VOLUNTEER_MESSAGE_ROLES_MAX = 50;
/** Order-preserving dedupe (the first occurrence wins). */
function dedupe(ids) {
    return [...new Set(ids)];
}
/** The chosen roles: 1..50 uuids as sent (the cap is on the raw input), then deduped, in the order given. */
const roleIdsSchema = z.array(uuid).min(1).max(VOLUNTEER_MESSAGE_ROLES_MAX).transform(dedupe);
/** The fields a crew email is written with (the send and the test share them). */
const volunteerMessageFields = {
    scope: z.enum(VOLUNTEER_MESSAGE_SCOPES),
    /** The chosen roles (`role` only): deduped, order kept; null / absent for `everyone`. */
    role_ids: roleIdsSchema.nullable().optional(),
    audience: z.enum(VOLUNTEER_MESSAGE_AUDIENCES).default("all"),
    include_waitlist: z.boolean().default(false),
    /** The sender opts in to replies (Reply-To = their account email); off by default. */
    allow_replies: z.boolean().default(false),
    subject: surveyText(VOLUNTEER_MESSAGE_SUBJECT_MAX, { min: 1 }),
    body: surveyText(VOLUNTEER_MESSAGE_BODY_MAX, { multiline: true }).default(""),
};
/** `role_ids` (at least one) is required for `role` and refused for `everyone`. */
function refineScopeRole(v, ctx) {
    if (v.scope === "role" && !v.role_ids?.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["role_ids"], message: "A role message needs role_ids" });
    }
    if (v.scope === "everyone" && v.role_ids) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["role_ids"], message: "An everyone message takes no role" });
    }
}
/**
 * POST …/messages. Strict. `role_ids` (1..50, deduped, order kept) is
 * required for `role` and refused for `everyone`; `audience: "led"` only with
 * `role` and exactly one role (the SQL further refuses it unless the caller is
 * an event lead). The subject is one line (newlines are
 * scrubbed to spaces), required; the body is the email Markdown subset,
 * possibly empty.
 */
export const volunteerMessageSendSchema = z
    .object(volunteerMessageFields)
    .strict()
    .superRefine((v, ctx) => {
    refineScopeRole(v, ctx);
    if (v.scope === "everyone" && v.audience !== "all") {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["audience"], message: "Only a role message has an audience" });
    }
    if (v.scope === "role" && v.audience === "led" && v.role_ids && v.role_ids.length !== 1) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["audience"], message: "Only a one-role message has the led audience" });
    }
});
/**
 * POST …/messages/test: the message as written, emailed only to the caller's
 * own account address (never chosen by the client). The same scope / role /
 * subject / body rules as the send; no audience or waitlist (one recipient).
 * `allow_replies` follows the compose tick, so the test shows the Reply-To and
 * footer the volunteers will get. `locale` is the sender's UI language (the email chrome and the `[Test]` prefix).
 */
export const volunteerMessageTestSchema = z
    .object({
    scope: volunteerMessageFields.scope,
    role_ids: volunteerMessageFields.role_ids,
    subject: volunteerMessageFields.subject,
    body: volunteerMessageFields.body,
    allow_replies: volunteerMessageFields.allow_replies,
    locale: z.enum(VOLUNTEER_LOCALES).default("en"),
})
    .strict()
    .superRefine(refineScopeRole);
/**
 * GET …/messages?scope=role&role_ids=<id>,<id> | ?scope=everyone. `role_ids`
 * is one comma-separated query parameter, parsed to a deduped uuid array
 * (order kept; empty segments dropped).
 */
export const volunteerMessageListQuerySchema = z
    .object({
    scope: z.enum(VOLUNTEER_MESSAGE_SCOPES),
    role_ids: z
        .string()
        .transform((raw) => raw
        .split(",")
        .map((id) => id.trim())
        .filter((id) => id !== ""))
        .pipe(roleIdsSchema)
        .optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.scope === "role" && !v.role_ids?.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["role_ids"], message: "A role history needs role_ids" });
    }
    if (v.scope === "everyone" && v.role_ids) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["role_ids"], message: "The everyone history takes no role" });
    }
});
/** POST /api/v1/public/volunteer-unsubscribe — the page's read (confirm false) and confirm (true). */
export const volunteerUnsubscribeSchema = z
    .object({
    token: z.string().regex(VOLUNTEER_UNSUBSCRIBE_TOKEN_RE, "not an unsubscribe token"),
    confirm: z.boolean().default(false),
})
    .strict();
// ---------------------------------------------------------------------------
// Coded errors
// ---------------------------------------------------------------------------
/** RV021..RV024 → HTTP status + coded envelope (the API maps; the web reads `code`). */
export const VOLUNTEER_MESSAGE_SQLSTATE_ERRORS = {
    RV021: { status: 403, code: VOLUNTEER_ERROR_CODES.messageNotAllowed },
    RV022: { status: 429, code: VOLUNTEER_ERROR_CODES.messageLimit },
    RV023: { status: 409, code: VOLUNTEER_ERROR_CODES.messageNoRecipients },
    RV024: { status: 422, code: VOLUNTEER_ERROR_CODES.messageTooMany },
};
/** `VOLUNTEER_MESSAGE_LIMIT|<retry_at ISO>|<limit>` (RV022), or null. */
export function parseVolunteerMessageLimit(message) {
    if (typeof message !== "string")
        return null;
    const m = /VOLUNTEER_MESSAGE_LIMIT\|(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z)\|(\d+)/.exec(message);
    return m ? { retryAt: m[1], limit: Number(m[2]) } : null;
}
/** `VOLUNTEER_MESSAGE_TOO_MANY|<count>|<cap>` (RV024), or null. */
export function parseVolunteerMessageTooMany(message) {
    if (typeof message !== "string")
        return null;
    const m = /VOLUNTEER_MESSAGE_TOO_MANY\|(\d+)\|(\d+)/.exec(message);
    return m ? { count: Number(m[1]), cap: Number(m[2]) } : null;
}
//# sourceMappingURL=volunteerMessages.js.map